import * as S from './style';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import { Theme } from '@/styles/Theme';
import { useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { Box, BoxWrapper, SubTitle } from '../style';
import type { PaymentInfo, GetCompanyInfoResponseProps } from '@/types/company.types';
import { dateFormat, dDayFormat } from '@/utils/convertDateFormat';
import Spacer from '@/components/common/Spacer';
import UpgradePlanModal from '../UpgradePlanModal';

interface Props {
  paymentData: PaymentInfo;
  companyData?: GetCompanyInfoResponseProps;
}

const UserPaymentInfo = ({ paymentData, companyData }: Props) => {
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false);

  const {
    activeWorkerCount,
    firstPaymentDate,
    isTrial,
    nearPaymentDate,
    numberOfNearPayments,
    pricePerWorker,
  } = paymentData;

  // Subscription status display
  const subscriptionStatus = companyData?.subscriptionStatus || 'free';
  const subscriptionEndDate = companyData?.subscriptionEndDate;
  const freeWorkersLimit = companyData?.freeWorkersLimit || 100;
  const freeInspectionsLimit = companyData?.freeInspectionsLimit || 100;

  const getSubscriptionStatusLabel = () => {
    switch (subscriptionStatus) {
      case 'active':
        return '유료 플랜';
      case 'expired':
        return '만료됨';
      default:
        return '무료 플랜';
    }
  };

  const getSubscriptionStatusColor = () => {
    switch (subscriptionStatus) {
      case 'active':
        return Theme.colors.B_3;
      case 'expired':
        return Theme.colors.G_2;
      default:
        return Theme.colors.G_1;
    }
  };

  return (
    <BoxWrapper>
      <SubTitle>사용 현황</SubTitle>
      <Box>
        <S.FlexContainer gap='0.5rem'>
          <Text fontSize='1.25rem' fontWeight={500} color={Theme.colors.DG_1}>
            현재 {activeWorkerCount}명의 작업자 계정 사용 중
          </Text>
          {companyData && (
            <S.SubscriptionBadge color={getSubscriptionStatusColor()}>
              {getSubscriptionStatusLabel()}
            </S.SubscriptionBadge>
          )}
        </S.FlexContainer>
        {companyData && subscriptionStatus === 'free' && (
          <>
            <S.UpgradeButtonWrapper>
              <Button
                backgroundColor={activeWorkerCount === 0 ? Theme.colors.G_5 : Theme.colors.B_3}
                color={activeWorkerCount === 0 ? Theme.colors.G_2 : Theme.colors.WHITE}
                height='2.5rem'
                onClick={() => setIsUpgradeModalOpen(true)}
                disabled={activeWorkerCount === 0}
              >
                플랜 업그레이드
              </Button>
            </S.UpgradeButtonWrapper>
            {activeWorkerCount === 0 && (
              <Text fontSize='0.875rem' color={Theme.colors.G_2} margin='0.5rem 0 0 0'>
                플랜 업그레이드를 하려면 최소 1명의 작업자를 추가해주세요.
              </Text>
            )}
          </>
        )}
        {companyData && subscriptionStatus === 'active' && subscriptionEndDate && (
          <Text fontSize='0.875rem' color={Theme.colors.G_1} margin='0.5rem 0 0 0'>
            구독 만료일: {dateFormat(subscriptionEndDate)}
          </Text>
        )}
        {companyData && subscriptionStatus === 'active' && nearPaymentDate && (
          <Text fontSize='1.625rem' fontWeight={700} margin='-0.9375rem 0 0 0'>
            {`${dateFormat(nearPaymentDate)}(D-${dDayFormat(
              nearPaymentDate
            )}) `}
            <Text fontSize='1.625rem' fontWeight={700} color={Theme.colors.B_3}>
              {`${(pricePerWorker * numberOfNearPayments).toLocaleString(
                'ko-KR'
              )}원 `}
            </Text>
            결제 예정
          </Text>
        )}

        <div>
          <S.FlexContainer
            cursor='pointer'
            onClick={() => setIsDetailOpen(!isDetailOpen)}
          >
            <Text color={Theme.colors.DG_1}>
              세부사항 {isDetailOpen ? '숨기기' : '보기'}
            </Text>
            <S.IconWrapper paddingTop='0.1875rem'>
              <IoIosArrowDown
                size='1.1875rem'
                color={Theme.colors.G_1}
                style={{
                  transform: `rotate(${isDetailOpen ? '180deg' : '0'})`,
                }}
              />
            </S.IconWrapper>
          </S.FlexContainer>
          <S.DetailContainer isActive={isDetailOpen}>
            <S.InnerDetailContainer>
              {subscriptionStatus === 'active' && firstPaymentDate && (
                <>
                  <S.DetailLine>
                    <Text>결제 시작일</Text>
                    <Text>{dateFormat(firstPaymentDate)}</Text>
                  </S.DetailLine>
                  <S.DetailLine>
                    <Text>작업자별 결제 금액</Text>
                    <Text>{pricePerWorker?.toLocaleString('ko-KR')}원</Text>
                  </S.DetailLine>
                  <Spacer y='1rem' />
                  {nearPaymentDate && (
                    <>
                      <S.DetailLine>
                        <Text>다음 결제일</Text>
                        <Text>{dateFormat(nearPaymentDate)}</Text>
                      </S.DetailLine>
                      <S.DetailLine>
                        <Text>다음 결제 작업자 수</Text>
                        <Text>{numberOfNearPayments}명</Text>
                      </S.DetailLine>
                      <S.DetailLine>
                        <Text>다음 결제 금액</Text>
                        <Text>
                          {(pricePerWorker * numberOfNearPayments).toLocaleString(
                            'ko-KR'
                          )}
                          원
                        </Text>
                      </S.DetailLine>
                    </>
                  )}
                </>
              )}
              {subscriptionStatus === 'free' && (
                <Text fontSize='0.875rem' color={Theme.colors.G_2}>
                  작업자당 월 요금: {pricePerWorker?.toLocaleString('ko-KR') || '220,000'}원
                  <br />
                  업그레이드 시 무제한 사용 가능
                </Text>
              )}
              {subscriptionStatus === 'expired' && (
                <>
                  {subscriptionEndDate && (
                    <S.DetailLine>
                      <Text>구독 만료일</Text>
                      <Text>{dateFormat(subscriptionEndDate)}</Text>
                    </S.DetailLine>
                  )}
                  <Text fontSize='0.875rem' color={Theme.colors.G_2} margin='0.5rem 0 0 0'>
                    구독을 갱신하면 모든 기능을 다시 사용할 수 있습니다
                  </Text>
                </>
              )}
            </S.InnerDetailContainer>
          </S.DetailContainer>
        </div>
      </Box>
      {isUpgradeModalOpen && (
        <UpgradePlanModal
          setIsModalOpen={setIsUpgradeModalOpen}
          activeWorkerCount={activeWorkerCount}
          pricePerWorker={pricePerWorker}
        />
      )}
    </BoxWrapper>
  );
};

export default UserPaymentInfo;
